/**
 * RoamerCareerModule.ts — Module #7: the Roamer Career™.
 *
 * A platform module (NOT a core engine): the professional growth center —
 * career profile, skills intelligence, income intelligence, client
 * management, work insights, career goals, professional analytics,
 * achievements, and privacy controls. Module pattern:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound ONLY via Core bus events + manual entry:
 *    - PaymentReceived ({amount, ...}) → auto-recorded income (source
 *      'payment'), which also progresses income goals.
 *    - MissionCompleted → mission completion counter (analytics context).
 *  • Outbound: achievements and completed goals publish as NotificationSent
 *    kinds 'careerAchievement' / 'careerGoalCompleted' — once-only,
 *    deterministic, fired ids persisted.
 *  • Honesty: intelligence/insights derive exclusively from recorded data
 *    with explicit basis; market wisdom requires sourced MarketSignals;
 *    forecasts need ≥3 full months and are labeled projections; achievements
 *    carry the recorded evidence that unlocked them.
 *  • AI coaching (Roamer AI Concierge™) is a registry interface only —
 *    future integration, no implementation.
 *  • Privacy is opt-in, off by default: profile/income/client/analytics
 *    visibility toggles gate the export sections.
 *  • Persistence nomad:career:v1 with record-by-record sanitization, caps,
 *    deep clones at read boundaries; export + deleteAll; injectable
 *    timestamps; handler errors never break the bus.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  AUTO_TRACKED_CAREER_GOAL_TYPES,
  CAREER_GOAL_TYPES,
  DEFAULT_CAREER_PRIVACY,
  DEFAULT_CAREER_PROFILE,
  type CareerAchievement,
  type CareerAchievementId,
  type CareerClient,
  type CareerCoach,
  type CareerExport,
  type CareerGoal,
  type CareerGoalType,
  type CareerInsight,
  type CareerPrivacySettings,
  type CareerProfile,
  type CareerProject,
  type CareerSkill,
  type ClientStatus,
  type IncomeIntelligence,
  type IncomeRecord,
  type IncomeSource,
  type MarketSignal,
  type ProfessionalAnalytics,
  type SkillsIntelligence,
} from './models';
import { computeIncomeIntelligence, computeProfessionalAnalytics } from './analytics';
import { computeInsights } from './insights';

const STORAGE_KEY = 'nomad:career:v1';
const MODULE_ID = 'roamer-career-module';
const MAX_SKILLS = 200;
const MAX_MARKET_SIGNALS = 300;
const MAX_INCOME_RECORDS = 2000;
const MAX_CLIENTS = 300;
const MAX_PROJECTS = 1000;
const MAX_GOALS = 200;

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}
function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asPositiveOrNull(v: unknown): number | null {
  const n = asNumberOrNull(v);
  return n != null && n > 0 ? n : null;
}
function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}

// ── AI coaching registry (future — interfaces only) ──────────────────────────

const coaches = new Map<string, CareerCoach>();

export function registerCareerCoach(c: CareerCoach): boolean {
  if (coaches.has(c.id)) return false;
  coaches.set(c.id, c);
  return true;
}
export function getCareerCoaches(): CareerCoach[] {
  return Array.from(coaches.values());
}

interface PersistedState {
  profile: CareerProfile;
  privacy: CareerPrivacySettings;
  skills: CareerSkill[];
  marketSignals: MarketSignal[];
  incomeRecords: IncomeRecord[];
  clients: CareerClient[];
  projects: CareerProject[];
  goals: CareerGoal[];
  achievements: CareerAchievement[];
  missionsCompleted: number;
}

export type NewIncomeInput = Omit<IncomeRecord, 'id'>;
export type NewClientInput = Omit<CareerClient, 'id' | 'addedAt'>;
export type NewProjectInput = Omit<CareerProject, 'id' | 'addedAt'>;

const INCOME_SOURCES: IncomeSource[] = ['project', 'mission', 'payment', 'other'];
const CLIENT_STATUSES: ClientStatus[] = ['active', 'past'];

export class RoamerCareerModule {
  private profile: CareerProfile = structuredClone(DEFAULT_CAREER_PROFILE);
  private privacy: CareerPrivacySettings = { ...DEFAULT_CAREER_PRIVACY };
  private skills: CareerSkill[] = [];
  private marketSignals: MarketSignal[] = [];
  private incomeRecords: IncomeRecord[] = [];
  private clients: CareerClient[] = [];
  private projects: CareerProject[] = [];
  private goals: CareerGoal[] = [];
  private achievements: CareerAchievement[] = [];
  private missionsCompleted = 0;
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration ────────────────────────────────────────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Career™',
      version: '1.0.0',
      capabilities: ['career-profile', 'skills-intelligence', 'income-intelligence', 'client-management', 'career-goals'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const safe = (handler: (e: CoreEvent) => void) => (e: CoreEvent) => {
      try {
        handler(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    };
    this.unsubscribes.push(core.on('PaymentReceived', safe((e) => this.onPaymentReceived(e)), 'module:career'));
    this.unsubscribes.push(core.on('MissionCompleted', safe((e) => this.onMissionCompleted(e)), 'module:career'));
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  /** Read-only: whether the module is currently attached to Roamer Core. */
  isAttached(): boolean {
    return this.core != null;
  }

  private publishNotification(kind: string, payload: Record<string, unknown>, at: number): void {
    try {
      this.core?.publish('NotificationSent', MODULE_ID, { kind, ...payload }, at);
    } catch {
      /* best-effort */
    }
  }

  private onPaymentReceived(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const amount = asPositiveOrNull(p.amount);
    if (amount == null) return;
    this.recordIncome({
      amount,
      currency: asString(p.currency) ?? this.profile.currency,
      source: 'payment',
      clientId: null,
      hoursWorked: null,
      note: 'Auto-recorded from PaymentReceived',
      at: e.at,
    }, e.at);
  }

  private onMissionCompleted(e: CoreEvent): void {
    this.missionsCompleted += 1;
    this.checkAchievements(e.at);
    this.afterUpdate();
  }

  getMissionsCompleted(): number {
    return this.missionsCompleted;
  }

  // ── Profile ─────────────────────────────────────────────────────────────────

  getProfile(): CareerProfile {
    return structuredClone(this.profile);
  }

  updateProfile(patch: Partial<CareerProfile>, at: number = Date.now()): CareerProfile {
    const portfolioTouched = patch.portfolioLinks != null &&
      JSON.stringify(patch.portfolioLinks) !== JSON.stringify(this.profile.portfolioLinks);
    this.profile = { ...this.profile, ...patch };
    if (portfolioTouched && patch.portfolioUpdatedAt === undefined) {
      this.profile.portfolioUpdatedAt = at;
    }
    this.checkAchievements(at);
    this.afterUpdate();
    return this.getProfile();
  }

  // ── Skills intelligence ─────────────────────────────────────────────────────

  addSkill(input: Omit<CareerSkill, 'addedAt'>, at: number = Date.now()): CareerSkill | null {
    const name = asString(input.name);
    if (!name) return null;
    if (this.skills.some((s) => s.name.toLowerCase() === name.toLowerCase())) return null;
    // Honesty: a verified skill needs a recorded verification note.
    if (input.verified && !asString(input.verificationNote)) return null;
    const skill: CareerSkill = { ...input, name, addedAt: at };
    this.skills.push(skill);
    if (this.skills.length > MAX_SKILLS) this.skills = this.skills.slice(-MAX_SKILLS);
    this.checkAchievements(at);
    this.afterUpdate();
    return structuredClone(skill);
  }

  updateSkill(name: string, patch: Partial<Omit<CareerSkill, 'name' | 'addedAt'>>, at: number = Date.now()): boolean {
    const skill = this.skills.find((s) => s.name.toLowerCase() === name.toLowerCase());
    if (!skill) return false;
    const next = { ...skill, ...patch };
    if (next.verified && !asString(next.verificationNote)) return false;
    Object.assign(skill, patch);
    this.checkAchievements(at);
    this.afterUpdate();
    return true;
  }

  getSkills(): CareerSkill[] {
    return structuredClone(this.skills);
  }

  addMarketSignal(input: Omit<MarketSignal, 'id'>, at: number = Date.now()): MarketSignal | null {
    if (!asString(input.name) || !asString(input.source)) return null;
    if (!['inDemandSkill', 'trendingTechnology', 'recommendedCertification'].includes(input.kind)) return null;
    const signal: MarketSignal = { ...input, id: `signal-${at}-${this.marketSignals.length}` };
    this.marketSignals.push(signal);
    if (this.marketSignals.length > MAX_MARKET_SIGNALS) {
      this.marketSignals = this.marketSignals.slice(-MAX_MARKET_SIGNALS);
    }
    this.afterUpdate();
    return structuredClone(signal);
  }

  getSkillsIntelligence(): SkillsIntelligence {
    const skillNames = new Set(this.skills.map((s) => s.name.toLowerCase()));
    const inDemand = this.marketSignals.filter((m) => m.kind === 'inDemandSkill');
    return structuredClone({
      verifiedSkills: this.skills.filter((s) => s.verified).map((s) => s.name),
      unverifiedSkills: this.skills.filter((s) => !s.verified).map((s) => s.name),
      learningGoals: this.skills.filter((s) => s.learningGoal).map((s) => s.name),
      strengths: inDemand.filter((m) => skillNames.has(m.name.toLowerCase())).map((m) => m.name),
      skillGaps: inDemand.filter((m) => !skillNames.has(m.name.toLowerCase())).map((m) => m.name),
      trendingTechnologies: this.marketSignals.filter((m) => m.kind === 'trendingTechnology').map((m) => m.name),
      recommendedCertifications: this.marketSignals.filter((m) => m.kind === 'recommendedCertification').map((m) => m.name),
      marketDataAvailable: this.marketSignals.length > 0,
    });
  }

  // ── Income ──────────────────────────────────────────────────────────────────

  recordIncome(input: NewIncomeInput, at: number = Date.now()): IncomeRecord | null {
    if (!(input.amount > 0) || !INCOME_SOURCES.includes(input.source)) return null;
    const record: IncomeRecord = {
      ...input,
      currency: asString(input.currency) ?? this.profile.currency,
      clientId: asString(input.clientId),
      hoursWorked: asPositiveOrNull(input.hoursWorked),
      note: input.note ?? '',
      id: `income-${at}-${this.incomeRecords.length}`,
    };
    this.incomeRecords.push(record);
    if (this.incomeRecords.length > MAX_INCOME_RECORDS) {
      this.incomeRecords = this.incomeRecords.slice(-MAX_INCOME_RECORDS);
    }
    // Auto-progress income goals recorded in the same currency.
    for (const goal of this.goals) {
      if (goal.type !== 'income' || goal.completedAt != null) continue;
      goal.currentAmount += record.currency === this.profile.currency ? record.amount : 0;
      this.maybeCompleteGoal(goal, at);
    }
    this.checkAchievements(at);
    this.afterUpdate();
    return structuredClone(record);
  }

  getIncomeRecords(): IncomeRecord[] {
    return structuredClone(this.incomeRecords);
  }

  getIncomeIntelligence(now: number = Date.now()): IncomeIntelligence {
    return structuredClone(computeIncomeIntelligence(this.incomeRecords, this.profile.currency, now));
  }

  // ── Clients & projects ──────────────────────────────────────────────────────

  addClient(input: NewClientInput, at: number = Date.now()): CareerClient | null {
    if (!asString(input.name) || !CLIENT_STATUSES.includes(input.status)) return null;
    const rating = asNumberOrNull(input.rating);
    const client: CareerClient = {
      ...input,
      name: input.name.trim(),
      country: asString(input.country),
      rating: rating != null && rating >= 1 && rating <= 5 ? rating : null,
      relationshipNotes: input.relationshipNotes ?? '',
      favorite: input.favorite === true,
      id: `client-${at}-${this.clients.length}`,
      addedAt: at,
    };
    this.clients.push(client);
    if (this.clients.length > MAX_CLIENTS) this.clients = this.clients.slice(-MAX_CLIENTS);
    this.checkAchievements(at);
    this.afterUpdate();
    return structuredClone(client);
  }

  updateClient(id: string, patch: Partial<Omit<CareerClient, 'id' | 'addedAt'>>, at: number = Date.now()): boolean {
    const client = this.clients.find((c) => c.id === id);
    if (!client) return false;
    if (patch.rating !== undefined && patch.rating !== null &&
        (typeof patch.rating !== 'number' || patch.rating < 1 || patch.rating > 5)) return false;
    Object.assign(client, patch);
    this.checkAchievements(at);
    this.afterUpdate();
    return true;
  }

  getClients(filter?: { status?: ClientStatus; favoritesOnly?: boolean }): CareerClient[] {
    let list = this.clients;
    if (filter?.status) list = list.filter((c) => c.status === filter.status);
    if (filter?.favoritesOnly) list = list.filter((c) => c.favorite);
    return structuredClone(list);
  }

  /** Payment history for a client — recorded income records only. */
  getClientPaymentHistory(clientId: string): IncomeRecord[] {
    return structuredClone(this.incomeRecords.filter((r) => r.clientId === clientId));
  }

  addProject(input: NewProjectInput, at: number = Date.now()): CareerProject | null {
    if (!asString(input.title)) return null;
    const project: CareerProject = {
      ...input,
      title: input.title.trim(),
      clientId: asString(input.clientId),
      skills: asStringArray(input.skills),
      income: asPositiveOrNull(input.income),
      currency: asString(input.currency) ?? this.profile.currency,
      accepted: typeof input.accepted === 'boolean' ? input.accepted : null,
      completedAt: asNumberOrNull(input.completedAt),
      id: `project-${at}-${this.projects.length}`,
      addedAt: at,
    };
    this.projects.push(project);
    if (this.projects.length > MAX_PROJECTS) this.projects = this.projects.slice(-MAX_PROJECTS);
    // Auto-progress client + freelance goals on completion; achievements too.
    if (project.completedAt != null) this.progressCountGoals(at);
    this.checkAchievements(at);
    this.afterUpdate();
    return structuredClone(project);
  }

  updateProject(id: string, patch: Partial<Omit<CareerProject, 'id' | 'addedAt'>>, at: number = Date.now()): boolean {
    const project = this.projects.find((p) => p.id === id);
    if (!project) return false;
    const wasCompleted = project.completedAt != null;
    Object.assign(project, patch);
    if (!wasCompleted && project.completedAt != null) this.progressCountGoals(at);
    this.checkAchievements(at);
    this.afterUpdate();
    return true;
  }

  getProjects(): CareerProject[] {
    return structuredClone(this.projects);
  }

  // ── Goals ───────────────────────────────────────────────────────────────────

  addGoal(input: { type: CareerGoalType; title: string; targetAmount: number; targetDate?: number | null }, at: number = Date.now()): CareerGoal | null {
    if (!CAREER_GOAL_TYPES.includes(input.type) || !asString(input.title) || !(input.targetAmount > 0)) return null;
    const goal: CareerGoal = {
      id: `goal-${at}-${this.goals.length}`,
      type: input.type,
      title: input.title.trim(),
      targetAmount: input.targetAmount,
      currentAmount: 0,
      targetDate: asNumberOrNull(input.targetDate),
      createdAt: at,
      completedAt: null,
    };
    this.goals.push(goal);
    if (this.goals.length > MAX_GOALS) this.goals = this.goals.slice(-MAX_GOALS);
    this.afterUpdate();
    return structuredClone(goal);
  }

  /** Manual progress for non-auto-tracked goal types. */
  progressGoal(id: string, amount: number, at: number = Date.now()): boolean {
    const goal = this.goals.find((g) => g.id === id);
    if (!goal || goal.completedAt != null || !(amount > 0)) return false;
    if (AUTO_TRACKED_CAREER_GOAL_TYPES.includes(goal.type)) return false; // income auto-tracks
    goal.currentAmount += amount;
    this.maybeCompleteGoal(goal, at);
    this.afterUpdate();
    return true;
  }

  getGoals(): CareerGoal[] {
    return structuredClone(this.goals);
  }

  private progressCountGoals(at: number): void {
    // A completed project counts one unit toward freelance/business goals.
    for (const goal of this.goals) {
      if (goal.completedAt != null) continue;
      if (goal.type !== 'freelance' && goal.type !== 'business') continue;
      goal.currentAmount += 1;
      this.maybeCompleteGoal(goal, at);
    }
  }

  private maybeCompleteGoal(goal: CareerGoal, at: number): void {
    if (goal.completedAt == null && goal.currentAmount >= goal.targetAmount) {
      goal.completedAt = at;
      this.publishNotification('careerGoalCompleted', { goalId: goal.id, type: goal.type, title: goal.title }, at);
    }
  }

  // ── Insights & analytics ────────────────────────────────────────────────────

  getInsights(now: number = Date.now()): CareerInsight[] {
    return structuredClone(computeInsights({
      profile: this.profile,
      skills: this.skills,
      marketSignals: this.marketSignals,
      incomeRecords: this.incomeRecords,
      projects: this.projects,
      now,
    }));
  }

  getAnalytics(now: number = Date.now()): ProfessionalAnalytics {
    return structuredClone(computeProfessionalAnalytics(this.projects, this.clients, this.incomeRecords, now));
  }

  // ── Achievements (once-only, evidence-based) ────────────────────────────────

  getAchievements(): CareerAchievement[] {
    return structuredClone(this.achievements);
  }

  private checkAchievements(at: number): void {
    const unlocked = new Set(this.achievements.map((a) => a.id));
    const unlock = (id: CareerAchievementId, title: string, evidence: string) => {
      if (unlocked.has(id)) return;
      unlocked.add(id);
      this.achievements.push({ id, title, unlockedAt: at, evidence });
      this.publishNotification('careerAchievement', { achievementId: id, title, evidence }, at);
    };

    const completed = this.projects.filter((p) => p.completedAt != null);
    const totalEarned = this.incomeRecords
      .filter((r) => r.currency === this.profile.currency)
      .reduce((s, r) => s + r.amount, 0);

    if (completed.length >= 1) unlock('firstRemoteJob', 'First Remote Job', `first completed project: ${completed[0].title}`);
    if (totalEarned >= 1000) unlock('earned1000', '$1,000 Earned', `${this.profile.currency} ${Math.floor(totalEarned)} recorded`);
    if (totalEarned >= 10000) unlock('earned10000', '$10,000 Earned', `${this.profile.currency} ${Math.floor(totalEarned)} recorded`);
    if (completed.length >= 100) unlock('projects100', '100 Projects', `${completed.length} completed projects recorded`);

    const projectsPerClient = new Map<string, number>();
    for (const p of completed) {
      if (p.clientId == null) continue;
      projectsPerClient.set(p.clientId, (projectsPerClient.get(p.clientId) ?? 0) + 1);
    }
    const repeatClients = Array.from(projectsPerClient.values()).filter((n) => n >= 2).length;
    if (repeatClients >= 5) unlock('fiveRepeatClients', 'Five Repeat Clients', `${repeatClients} clients with 2+ completed projects`);

    const rated = this.clients.filter((c) => c.rating != null);
    if (rated.length >= 5) {
      const avg = rated.reduce((s, c) => s + c.rating!, 0) / rated.length;
      if (avg >= 4.8) unlock('topRatedFreelancer', 'Top Rated Freelancer', `average client rating ${avg.toFixed(2)} across ${rated.length} recorded ratings`);
    }

    const verifiedCount = this.skills.filter((s) => s.verified).length;
    if (verifiedCount >= 5) unlock('skillMaster', 'Skill Master', `${verifiedCount} verified skills recorded`);

    const countries = new Set(this.clients.map((c) => c.country).filter((c): c is string => c != null));
    if (countries.size >= 3) unlock('globalProfessional', 'Global Professional', `clients recorded in ${countries.size} countries`);
  }

  // ── Privacy, export, delete ─────────────────────────────────────────────────

  getPrivacySettings(): CareerPrivacySettings {
    return { ...this.privacy };
  }

  updatePrivacySettings(patch: Partial<CareerPrivacySettings>): CareerPrivacySettings {
    this.privacy = { ...this.privacy, ...patch };
    this.afterUpdate();
    return this.getPrivacySettings();
  }

  exportCareer(now: number = Date.now()): CareerExport {
    let profile: CareerProfile | null = null;
    if (this.privacy.profileVisible) {
      profile = structuredClone(this.profile);
      if (!this.privacy.portfolioSharingEnabled) profile.portfolioLinks = [];
      if (!this.privacy.resumeSharingEnabled) profile.resumeVersions = [];
    }
    return structuredClone({
      exportedAt: now,
      profile,
      skills: this.skills,
      marketSignals: this.marketSignals,
      incomeRecords: this.privacy.incomeVisible ? this.incomeRecords : [],
      clients: this.privacy.clientInfoVisible ? this.clients : [],
      projects: this.projects,
      goals: this.goals,
      achievements: this.privacy.analyticsVisible ? this.achievements : [],
    });
  }

  deleteAll(): void {
    this.profile = structuredClone(DEFAULT_CAREER_PROFILE);
    this.privacy = { ...DEFAULT_CAREER_PRIVACY };
    this.skills = [];
    this.marketSignals = [];
    this.incomeRecords = [];
    this.clients = [];
    this.projects = [];
    this.goals = [];
    this.achievements = [];
    this.missionsCompleted = 0;
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        profile: this.profile,
        privacy: this.privacy,
        skills: this.skills,
        marketSignals: this.marketSignals,
        incomeRecords: this.incomeRecords,
        clients: this.clients,
        projects: this.projects,
        goals: this.goals,
        achievements: this.achievements,
        missionsCompleted: this.missionsCompleted,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('career', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Cloud hydration: reload in-memory state from localStorage after cloud adoption. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      const pr = (state.profile ?? {}) as Record<string, unknown>;
      this.profile = {
        primaryProfession: asString(pr.primaryProfession),
        yearsExperience: asPositiveOrNull(pr.yearsExperience),
        languages: asStringArray(pr.languages),
        certifications: asStringArray(pr.certifications),
        portfolioLinks: asStringArray(pr.portfolioLinks),
        portfolioUpdatedAt: asNumberOrNull(pr.portfolioUpdatedAt),
        resumeVersions: (Array.isArray(pr.resumeVersions) ? pr.resumeVersions : [])
          .filter((r): r is { id: string; label: string; ref: string; updatedAt?: unknown } =>
            r != null && typeof r === 'object' &&
            typeof (r as { id?: unknown }).id === 'string' &&
            typeof (r as { label?: unknown }).label === 'string' &&
            typeof (r as { ref?: unknown }).ref === 'string')
          .map((r) => ({ id: r.id, label: r.label, ref: r.ref, updatedAt: asNumberOrNull(r.updatedAt) ?? 0 })),
        preferredIndustries: asStringArray(pr.preferredIndustries),
        preferredClients: asStringArray(pr.preferredClients),
        hourlyRate: asPositiveOrNull(pr.hourlyRate),
        targetHourlyRate: asPositiveOrNull(pr.targetHourlyRate),
        monthlyIncomeGoal: asPositiveOrNull(pr.monthlyIncomeGoal),
        currency: asString(pr.currency) ?? 'USD',
        availabilityHoursPerWeek: asPositiveOrNull(pr.availabilityHoursPerWeek),
        remoteExperienceYears: asPositiveOrNull(pr.remoteExperienceYears),
        currentStatus: pr.currentStatus === 'available' || pr.currentStatus === 'booked' ||
          pr.currentStatus === 'openToOffers' || pr.currentStatus === 'onBreak' ? pr.currentStatus : 'unknown',
      };

      const pv = (state.privacy ?? {}) as Record<string, unknown>;
      this.privacy = {
        profileVisible: pv.profileVisible === true,
        portfolioSharingEnabled: pv.portfolioSharingEnabled === true,
        resumeSharingEnabled: pv.resumeSharingEnabled === true,
        incomeVisible: pv.incomeVisible === true,
        clientInfoVisible: pv.clientInfoVisible === true,
        analyticsVisible: pv.analyticsVisible === true,
      };

      this.skills = (Array.isArray(state.skills) ? state.skills : [])
        .filter((s): s is Record<string, unknown> =>
          s != null && typeof s === 'object' &&
          typeof (s as { name?: unknown }).name === 'string' &&
          (s as { name: string }).name.trim().length > 0)
        .map((s): CareerSkill => {
          const verificationNote = asString(s.verificationNote);
          return {
            name: (s.name as string).trim(),
            // Honesty invariant survives corrupt storage: verified needs a note.
            verified: s.verified === true && verificationNote != null,
            verificationNote,
            learningGoal: s.learningGoal === true,
            addedAt: asNumberOrNull(s.addedAt) ?? 0,
          };
        })
        .slice(-MAX_SKILLS);

      this.marketSignals = (Array.isArray(state.marketSignals) ? state.marketSignals : [])
        .filter((m): m is MarketSignal =>
          m != null && typeof m === 'object' &&
          typeof (m as MarketSignal).id === 'string' &&
          typeof (m as MarketSignal).name === 'string' &&
          typeof (m as MarketSignal).source === 'string' &&
          (m as MarketSignal).source.trim().length > 0 &&
          ['inDemandSkill', 'trendingTechnology', 'recommendedCertification'].includes((m as MarketSignal).kind))
        .map((m) => ({ ...m, note: typeof m.note === 'string' ? m.note : '', recordedAt: asNumberOrNull(m.recordedAt) ?? 0 }))
        .slice(-MAX_MARKET_SIGNALS);

      this.incomeRecords = (Array.isArray(state.incomeRecords) ? state.incomeRecords : [])
        .filter((r): r is Record<string, unknown> =>
          r != null && typeof r === 'object' &&
          typeof (r as { id?: unknown }).id === 'string' &&
          typeof (r as { amount?: unknown }).amount === 'number' &&
          (r as { amount: number }).amount > 0 &&
          INCOME_SOURCES.includes((r as { source?: never }).source as never))
        .map((r): IncomeRecord => ({
          id: r.id as string,
          amount: r.amount as number,
          currency: asString(r.currency) ?? 'USD',
          source: r.source as IncomeSource,
          clientId: asString(r.clientId),
          hoursWorked: asPositiveOrNull(r.hoursWorked),
          note: typeof r.note === 'string' ? r.note : '',
          at: asNumberOrNull(r.at) ?? 0,
        }))
        .slice(-MAX_INCOME_RECORDS);

      this.clients = (Array.isArray(state.clients) ? state.clients : [])
        .filter((c): c is Record<string, unknown> =>
          c != null && typeof c === 'object' &&
          typeof (c as { id?: unknown }).id === 'string' &&
          typeof (c as { name?: unknown }).name === 'string' &&
          CLIENT_STATUSES.includes((c as { status?: never }).status as never))
        .map((c): CareerClient => {
          const rating = asNumberOrNull(c.rating);
          return {
            id: c.id as string,
            name: c.name as string,
            status: c.status as ClientStatus,
            favorite: c.favorite === true,
            country: asString(c.country),
            rating: rating != null && rating >= 1 && rating <= 5 ? rating : null,
            relationshipNotes: typeof c.relationshipNotes === 'string' ? c.relationshipNotes : '',
            addedAt: asNumberOrNull(c.addedAt) ?? 0,
          };
        })
        .slice(-MAX_CLIENTS);

      this.projects = (Array.isArray(state.projects) ? state.projects : [])
        .filter((p): p is Record<string, unknown> =>
          p != null && typeof p === 'object' &&
          typeof (p as { id?: unknown }).id === 'string' &&
          typeof (p as { title?: unknown }).title === 'string')
        .map((p): CareerProject => ({
          id: p.id as string,
          title: p.title as string,
          clientId: asString(p.clientId),
          skills: asStringArray(p.skills),
          income: asPositiveOrNull(p.income),
          currency: asString(p.currency) ?? 'USD',
          accepted: typeof p.accepted === 'boolean' ? p.accepted : null,
          completedAt: asNumberOrNull(p.completedAt),
          addedAt: asNumberOrNull(p.addedAt) ?? 0,
        }))
        .slice(-MAX_PROJECTS);

      this.goals = (Array.isArray(state.goals) ? state.goals : [])
        .filter((g): g is Record<string, unknown> =>
          g != null && typeof g === 'object' &&
          typeof (g as { id?: unknown }).id === 'string' &&
          typeof (g as { title?: unknown }).title === 'string' &&
          CAREER_GOAL_TYPES.includes((g as { type?: never }).type as never) &&
          typeof (g as { targetAmount?: unknown }).targetAmount === 'number' &&
          (g as { targetAmount: number }).targetAmount > 0)
        .map((g): CareerGoal => {
          const current = asNumberOrNull(g.currentAmount);
          return {
            id: g.id as string,
            type: g.type as CareerGoalType,
            title: g.title as string,
            targetAmount: g.targetAmount as number,
            currentAmount: current != null && current >= 0 ? current : 0,
            targetDate: asNumberOrNull(g.targetDate),
            createdAt: asNumberOrNull(g.createdAt) ?? 0,
            completedAt: asNumberOrNull(g.completedAt),
          };
        })
        .slice(-MAX_GOALS);

      this.achievements = (Array.isArray(state.achievements) ? state.achievements : [])
        .filter((a): a is CareerAchievement =>
          a != null && typeof a === 'object' &&
          typeof (a as CareerAchievement).id === 'string' &&
          typeof (a as CareerAchievement).title === 'string' &&
          typeof (a as CareerAchievement).evidence === 'string');

      const missions = asNumberOrNull(state.missionsCompleted);
      this.missionsCompleted = missions != null && missions >= 0 ? missions : 0;
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerCareerModule = new RoamerCareerModule();

/**
 * Boot hydration: adopt cloud Career state if strictly newer than local.
 * Called by HydrationOrchestrator. Returns true if cloud data was adopted.
 * If no cloud record exists yet, migrates current local data to cloud (first-run).
 */
export async function hydrateCareerFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<PersistedState>('career', 'state');
  if (!cloud) {
    // Migrate: push existing local data to cloud on first run with cloud sync.
    try {
      if (typeof localStorage !== 'undefined') {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) storageManager.push('career', 'state', JSON.parse(raw) as PersistedState);
      }
    } catch { /* best-effort */ }
    return false;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud));
    }
    roamerCareerModule.reloadFromStorage();
    return true;
  } catch { return false; }
}
